import React, { Fragment } from 'react';

import type { ExtensionHandler, ExtensionHandlers } from '@atlaskit/editor-common/extensions';
import { getTemplateExtensionHandler } from './template-variable-extension/index.ts';

import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import { getTemplateMigrationExtHandler } from '@confluence/fabric-extension-handlers/entry-points/TemplateMigration.ts';
import { DeadMacroRenderer } from '@confluence/fabric-extension-handlers/entry-points/DeadMacroRenderer.ts';
import { LegacyMacroRenderer } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/LegacyMacroRendererQuery.ts';
import {
	getMacroAttributesFromADFNode,
	EDITOR,
	getExperienceName,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { Mode } from '@confluence/macro-tracker/entry-points/types.ts';
// oxlint-disable-next-line import/no-duplicates
import type { Extension } from '@confluence/macro-tracker/entry-points/types.ts';

type ExtensionHandlerProps = {
	node: Extension;
	contentId: string;
	mode: Mode;
	extensionKey: string;
	attributes?: ExperienceAttributes;
};

export const getTemplateExtensionHandlers = (): ExtensionHandlers => ({
	[EXTENSION_TYPE.MACRO_CORE]: getTemplateMacroExtensionHandler(),
	[EXTENSION_TYPE.MIGRATION]: getTemplateMigrationExtHandler(),
	[EXTENSION_TYPE.TEMPLATE]: getTemplateExtensionHandler,
});

export const getTemplateMacroExtensionHandler = (): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension) => {
		const { extensionKey } = extension;
		const attributes = getMacroAttributesFromADFNode(extension);
		const templateId = '0';

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			contentId: templateId,
			mode: 'editor' as Mode,
			extensionKey,
			attributes,
		};
		let implementation;

		switch (extensionKey) {
			// whitelist (CFE-2326)
			case 'detailssummary':
			case 'recently-updated':
			case 'children':
			case 'attachments':
			case 'contentbylabel':
			case 'profile-picture':
			case 'pagetree':
			case 'include':
			case 'tasks-report-macro':
			case 'profile':
			case 'widget':
			case 'excerpt-include':
			case 'spaces':
				implementation = <LegacyMacroRenderer {...macroDefaultProps} />;
				break;
			default:
				implementation = <DeadMacroRenderer {...macroDefaultProps} />;
		}

		return (
			<Fragment>
				<MacroExperienceStart
					name={getExperienceName(EDITOR, extension)}
					contentId={templateId}
					mode={EDITOR}
					extensionKey={extensionKey}
					attributes={attributes}
					timeout={ExperienceTimeout.MACRO_LOAD}
				/>
				{implementation}
			</Fragment>
		);
	});
